import React, { useEffect, useCallback } from 'react';
import eventEmitter from '../../utils/events';
import checkoutService from '../../api/checkoutService';
import { PaymentFields } from '../payment/PaymentFields';
import {
  setIsDeclinePopup,
  setDeclineReasonCode,
  setPurchaseToken,
  setCheckoutOrderID,
} from '../../redux/slices/checkoutSlice';
import {
  setPurchaseCompleted,
  setSubmitSuccess,
} from '../../redux/slices/commonSlice';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import {
  Address,
  CardInfo,
} from '../../../page-builder/interface/checkoutInterface';
import { IErrorResponse } from '../../../page-builder/interface/apiInterface';
import { resetState } from '../../../page-builder/redux/slices/postUpsellStateSlice';
import { PaymentProps } from './Payment';
import { showNotification } from '../../helper/common/commonFunctions';
import { StatusCode } from '../../api/constant';

type FormValues = {
  ID: string;
  CardInfo: CardInfo;
  BillingAddress: Address;
};

interface PaymentControllerProps {
  fields: PaymentProps;
  fromRadio?: boolean;
}

export const PaymentController: React.FC<PaymentControllerProps> = ({
  fields,
  fromRadio = false,
}) => {
  const cartData = useAppSelector(state => state.cart?.cartData);
  const storeData = useAppSelector(state => state.store.storeData);
  const dispatch = useAppDispatch();

  const handleReturnToPayment = () => {
    dispatch(setIsDeclinePopup(false));
  };

  const postCheckoutData = useCallback(
    async (input: FormValues) => {
      if (!cartData?.ID || !storeData?.StoreID || !storeData?.ShopID) return;
      const payload = {
        BillingAddress: input.BillingAddress,
        CardInfo: input.CardInfo,
        ID: cartData.ID,
        IdempotencyKey: input.ID,
        ShopID: storeData.ShopID,
        StoreID: storeData.StoreID,
      };
      dispatch(setSubmitSuccess(true));
      try {
        const response = await checkoutService.checkoutComplete(payload);
        dispatch(setPurchaseToken(response.data?.PurchaseToken));
        dispatch(setCheckoutOrderID(response.data?.OrderID));
        dispatch(resetState());
        showNotification(StatusCode.success, 'Checkout completed successfully');
        dispatch(setPurchaseCompleted(true));
      } catch (e) {
        const error = e as IErrorResponse;
        if (error?.status === 402) {
          dispatch(setIsDeclinePopup(true));
          dispatch(
            setDeclineReasonCode(error?.message || 'Invalid card information'),
          );

          dispatch(setSubmitSuccess(false));
        }
        if (error.status) {
          showNotification(StatusCode.failed, 'Checkout Failed');
          dispatch(setSubmitSuccess(false));
        }
      } finally {
        dispatch(setSubmitSuccess(false));
      }
    },
    [dispatch, cartData?.ID, storeData?.StoreID, storeData?.ShopID],
  );

  useEffect(() => {
    eventEmitter.addListener('completePurchase', postCheckoutData);
    return () => {
      eventEmitter.removeListener('completePurchase', postCheckoutData);
    };
  }, [postCheckoutData]);

  return (
    <>
      <PaymentFields
        handleReturnToPayment={handleReturnToPayment}
        fields={fields}
        fromRadio={fromRadio}
      />
    </>
  );
};
